import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import polPhoto from "@/assets/pol-egea.webp";
import { COOKIE_CONSENT_UPDATED_EVENT, getConsent } from "@/lib/cookieConsent";

/**
 * Pol Egea contact card.
 * - Appears only after the cookie notice is answered AND the visitor shows interest
 *   (scrolls ~60% of a screen or stays ENGAGE_MS on the site).
 * - The full card shows ONCE per session, slides up from the bottom-left and
 *   shrinks by itself to a small round photo after AUTO_COLLAPSE_MS.
 * - It never reopens on its own; the small photo only opens it when clicked.
 * - Stays out of the way: sits below the header, menus, forms and cookie notice,
 *   hides over the footer and on thank-you pages.
 */
const ENGAGE_MS = 15000;
const AUTO_COLLAPSE_MS = 8000;
const SESSION_KEY = "techtail_pol_card_seen";
const WHATSAPP_NUMBER = "34678311074";

const readSeen = () => {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
};
const markSeen = () => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
};

const DelayedLeadPopup = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [overFooter, setOverFooter] = useState(false);

  // Wait for cookie answer + real engagement before showing anything.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let consentGiven = !!getConsent();
    let engaged = false;
    let done = false;

    const tryShow = () => {
      if (done || !consentGiven || !engaged) return;
      done = true;
      cleanup();
      setReady(true);
      if (!readSeen()) {
        markSeen();
        setExpanded(true);
      }
    };
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.6) {
        engaged = true;
        tryShow();
      }
    };
    const onConsent = () => {
      consentGiven = true;
      tryShow();
    };
    const timer = window.setTimeout(() => {
      engaged = true;
      tryShow();
    }, ENGAGE_MS);
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, onConsent);
    };

    // Returning in the same session: go straight to the small photo after consent.
    if (readSeen()) engaged = true;
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, onConsent);
    tryShow();
    return cleanup;
  }, []);

  // The full card shrinks by itself; it never pops open again on its own.
  useEffect(() => {
    if (!expanded) return;
    const timer = window.setTimeout(() => setExpanded(false), AUTO_COLLAPSE_MS);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  // Close the card when the visitor changes page.
  useEffect(() => {
    setExpanded(false);
  }, [pathname]);

  // Hide over the footer so it never covers footer links.
  useEffect(() => {
    if (!ready) return;
    const onScroll = () => {
      const footer = document.getElementById("site-footer");
      setOverFooter(!!footer && footer.getBoundingClientRect().top < window.innerHeight - 16);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ready, pathname]);

  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    t("sChrome.advisorPopup.wa"),
  )}`;

  if (!ready || /\/gracias(\/|$)/.test(pathname)) return null;

  // Bottom-left corner, on the same row as the floating "estudio" button.
  // On mobile the open card sits just above that row so it never covers the button.
  const cardPosition = "fixed left-4 bottom-[76px] z-40 md:left-6 md:bottom-6";
  const bubblePosition = "fixed left-4 bottom-4 z-40 md:left-6 md:bottom-6";

  return (
    <AnimatePresence mode="wait" initial={false}>
      {overFooter ? null : expanded ? (
        <motion.div
          key="card"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          role="dialog"
          aria-label={t("sChrome.advisorPopup.greeting")}
          className={`${cardPosition} w-[230px] origin-bottom-left bg-bone text-[#0d0b0b] shadow-xl will-change-transform`}
        >
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label={t("sChrome.advisorPopup.close")}
            className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center bg-[#0d0b0b] text-bone transition-opacity hover:opacity-80"
          >
            <X className="h-3 w-3" />
          </button>

          <div className="flex items-center gap-2.5 p-2.5">
            <img
              src={polPhoto}
              alt="Pol Egea, Director de Desarrollo de Negocio en Techtail"
              loading="lazy"
              className="h-10 w-10 shrink-0 rounded-full object-cover object-top"
            />
            <div className="min-w-0">
              <p className="text-[15px] font-normal leading-tight">
                {t("sChrome.advisorPopup.instant")}
              </p>
              <p className="mt-0.5 text-[12px] leading-tight text-[#0d0b0b]/60">
                {t("sChrome.advisorPopup.role")}
              </p>
            </div>
          </div>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setExpanded(false)}
            className="block w-full bg-[#0d0b0b] px-3 py-2 text-center text-xs uppercase tracking-wide text-bone transition-opacity hover:opacity-90"
          >
            {t("sChrome.advisorPopup.cta")}
          </a>
        </motion.div>
      ) : (
        <motion.button
          key="bubble"
          type="button"
          onClick={() => setExpanded(true)}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.6, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          aria-label={t("sChrome.advisorPopup.tab")}
          title={t("sChrome.advisorPopup.instant")}
          className={`${bubblePosition} group h-12 w-12 rounded-full bg-bone p-0.5 shadow-xl transition-transform hover:scale-105`}
        >
          <img
            src={polPhoto}
            alt=""
            loading="lazy"
            className="h-full w-full rounded-full object-cover object-top"
          />
          <span
            aria-hidden
            className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-bone bg-[#25D366]"
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default DelayedLeadPopup;
